import { Color, Vector3 } from "three";
import { PointCloudMaterial } from "../utils/PointCloudMaterial";
import { IPointcloudSectionSelection } from "./PointCloud";
import { Viewer } from "../viewer";

export class PointsMaterialHandler {
  private _viewer: Viewer;
  private _pointMaterial: PointCloudMaterial;

  constructor(viewer: Viewer) {
    this._viewer = viewer;
    this._pointMaterial = this.initRgbMaterial();
  }

  initRgbMaterial() {
    return new PointCloudMaterial(
      {
        vertexColors: true,
        size: this._viewer.getPointSize(),
        sizeAttenuation: false,
        transparent: true,
      },
      "rgb"
    );
  }

  createPickMaterial(index: number) {
    const pickMaterial = new PointCloudMaterial(
      {
        vertexColors: true,
        size: this._viewer.getPointSize(),
        sizeAttenuation: false,
        transparent: false,
      },
      "indices",
      index
    );

    pickMaterial.setCameraInfo(
      this._viewer.getRootLength(),
      this._viewer.getCamera().position
    );

    return pickMaterial;
  }

  public setCameraInfo(rootLength: number, perspectiveCameraPosition: Vector3) {
    this._pointMaterial.setCameraInfo(rootLength, perspectiveCameraPosition);
  }

  public get pointMaterial() {
    return this._pointMaterial;
  }

  public highlightSelectedPointCloudSection(
    selection: IPointcloudSectionSelection,
    color: Color
  ) {
    const midpoints = [];
    const planeNormals = [];
    const triangleIndices = [];
    let i = 0;
    const isSelectInsides = [];
    const isDeleteds = [];
    for (const shape of selection.convexShapes) {
      for (const plane of shape.planes) {
        midpoints.push(...plane.position.toArray());
        planeNormals.push(...plane.normal.toArray());
        triangleIndices.push(i);
        ++i;
      }
    }

    let accumulatedShapeCount = 0;
    for (const shapeCount of selection.shapeCounts) {
      isSelectInsides.push(
        selection.convexShapes[accumulatedShapeCount].isSelectInside
      );
      isDeleteds.push(selection.convexShapes[accumulatedShapeCount].isDeleted);
      accumulatedShapeCount += shapeCount;
    }
    this._pointMaterial.shader.uniforms["shapeMidPoints"].value = midpoints;
    this._pointMaterial.shader.uniforms["shapeMidNormals"].value = planeNormals;
    this._pointMaterial.shader.uniforms["triangleIndices"].value =
      triangleIndices;
    this._pointMaterial.shader.uniforms["shapeCounts"].value =
      selection.shapeCounts;
    this._pointMaterial.shader.uniforms["highlightColor"].value = new Vector3(
      color.r,
      color.g,
      color.b
    );
    this._pointMaterial.shader.uniforms["isSelectInsides"].value =
      isSelectInsides;
    this._pointMaterial.shader.uniforms["isDeleteds"].value = isDeleteds;
    this._pointMaterial.shader.uniforms["totalNumberOfShapes"].value =
      selection.shapeCounts.length;
  }

  public resetHighlightSelectedPointCloudSection() {
    this._pointMaterial.shader.uniforms["shapeMidPoints"].value = [];
    this._pointMaterial.shader.uniforms["shapeMidNormals"].value = [];
    this._pointMaterial.shader.uniforms["triangleIndices"].value = [];
    this._pointMaterial.shader.uniforms["shapeCounts"].value = [];
    this._pointMaterial.shader.uniforms["highlightColor"].value = new Vector3(
      1,
      1,
      1
    );
    this._pointMaterial.shader.uniforms["isSelectInsides"].value = [];
    this._pointMaterial.shader.uniforms["isDeleteds"].value = [];
    this._pointMaterial.shader.uniforms["totalNumberOfShapes"].value = 0;
  }
}
